import type {
  HubSpotCompanyDetailV2,
  HubSpotCompanyNet4References,
  HubSpotCompanySearchPage,
} from "@/lib/hubspot/company-contract";
import {
  parseHubSpotCompanyDetailV2,
  parseHubSpotCompanyNet4References,
  parseHubSpotCompanySearchPage,
  parseHubSpotProblemDetails,
} from "@/lib/hubspot/company-contract";
import {
  fetchGatewayWithSessionRecovery,
  GatewayAuthRequiredError,
} from "@/lib/auth/gateway";
import { z } from "zod";

export class HubSpotCompanyServiceError extends Error {
  constructor(readonly status: number, readonly code: string) {
    super(`HubSpot company service request failed (${status}, ${code})`);
    this.name = "HubSpotCompanyServiceError";
  }
}
type ExternalPayload = Parameters<typeof parseHubSpotCompanySearchPage>[0];
type PayloadParser<T> = (value: ExternalPayload) => T | null;

const gatewayErrorSchema = z.object({
  code: z.string().min(1),
});

async function readPayload<T>(response: Response, parse: PayloadParser<T>): Promise<T> {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const problemDetails = parseHubSpotProblemDetails(payload);
    const gatewayError = gatewayErrorSchema.safeParse(payload);
    const code = problemDetails?.status === response.status
      ? problemDetails.code
      : gatewayError.success
        ? gatewayError.data.code
        : "HUBSPOT_REQUEST_FAILED";
    throw new HubSpotCompanyServiceError(response.status, code);
  }
  const parsedPayload = parse(payload);
  if (parsedPayload === null) {
    throw new HubSpotCompanyServiceError(502, "UPSTREAM_RESPONSE_INVALID");
  }
  return parsedPayload;
}

async function execute<T>(
  request: Request,
  parse: PayloadParser<T>,
): Promise<T> {
  const source = new URL(request.url);
  const headers = new Headers({
    Accept: "application/json",
    "X-Correlation-Id": crypto.randomUUID(),
  });
  const contentType = request.headers.get("content-type");
  if (contentType) headers.set("Content-Type", contentType);
  let response: Response;
  try {
    response = await fetchGatewayWithSessionRecovery(`${source.pathname}${source.search}`, {
      method: request.method,
      cache: request.cache,
      headers,
      body: request.method === "GET" || request.method === "HEAD" ? undefined : await request.text(),
    });
  } catch (error) {
    if (error instanceof GatewayAuthRequiredError) {
      throw new HubSpotCompanyServiceError(401, "AUTH_REQUIRED");
    }
    throw error;
  }
  return readPayload(response, parse);
}

export function searchCompaniesFromService(
  query: string,
  limit = 100,
  cursor?: string,
): Promise<HubSpotCompanySearchPage> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  return execute(
    new Request(`http://hubspot-bff.local/api/hubspot/v1/companies?${params}`, { cache: "no-store" }),
    parseHubSpotCompanySearchPage,
  );
}

export function getCompanyFromServiceV2(companyId: string): Promise<HubSpotCompanyDetailV2> {
  return execute(
    new Request(`http://hubspot-bff.local/api/hubspot/v2/companies/${encodeURIComponent(companyId)}`, { cache: "no-store" }),
    parseHubSpotCompanyDetailV2,
  );
}

export function getCompanyNet4ReferencesFromService(companyId: string): Promise<HubSpotCompanyNet4References> {
  return execute(
    new Request(`http://hubspot-bff.local/api/hubspot/v1/companies/${encodeURIComponent(companyId)}/net4-references`, { cache: "no-store" }),
    parseHubSpotCompanyNet4References,
  );
}
